import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Button, DatePicker, Select, Skeleton, Space, Table } from 'antd';
import { CloseCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { gold, green, grey, purple, red } from '@ant-design/colors';
import styled from 'styled-components';
import { useFinances } from './use-finances';
import { Formatting } from '../../utils';
import Page from 'components/page/page';
import FinancesPrint from 'prints/finances-print';
import { FinancesOrderView, getReadablePaymentType, getReadablePriceType, PaymentType, PriceType } from 'types';
import { ColumnsType } from 'antd/lib/table';
import SearchSelector from 'components/searchSelectorNew';

const OrdersTable = styled.div`
	flex: 1;
	height: 1px;

	> .ant-table-wrapper {
		height: 100%;

		.ant-table-row {
			&.red {
				> td:first-child {
					border-left: 8px solid ${red[5]};
				}
			}

			&.yellow {
				> td:first-child {
					border-left: 8px solid ${gold[5]};
				}
			}

			&.green {
				> td:first-child {
					border-left: 8px solid ${green[5]};
				}
			}

			&.purple {
				> td:first-child {
					border-left: 8px solid ${purple[5]};
				}
			}

			&.grey {
				> td:first-child {
					border-left: 8px solid ${grey[5]};
				}
			}
		}
	}
`;

export const Finances: React.FC = () => {
	const {
		clients,
		orders,
		from,
		to,
		clientId,
		paid,
		paymentType,
		fromChanged,
		toChanged,
		clientIdChanged,
		paidChanged,
		paymentTypeChanged,
		clearFilters,
	} = useFinances();

	const columns: ColumnsType<FinancesOrderView> = [
		{
			title: 'Klient',
			key: 'name',
			render: (_, row) => <Link to={`/panel/clients/${row.client.id}`}>{row.client.name}</Link>,
		},
		{
			title: 'Cena',
			key: 'price',
			render: (_, row) => {
				const price = row.priceType === PriceType.gross ? row.grossPrice : row.netPrice;

				return (
					<>
						{Formatting.money(price)} ({getReadablePriceType(row.priceType)})
					</>
				);
			},
		},
		{
			title: 'Faktura',
			key: 'invoice',
			render: (_, row) => row.invoice ?? '-',
		},
		{
			title: 'Typ płatności',
			key: 'payment_type',
			render: (_, row) => getReadablePaymentType(row.wantedPaymentType),
		},
		{
			title: 'Podstawienia',
			key: 'delivered',
			render: (_, row) => Formatting.epochToDate(row.deliveryDate, true),
		},
		{
			title: 'Odebrane',
			key: 'claimed',
			render: (_, row) => (row.claimingDate ? Formatting.epochToDate(row.claimingDate, true) : '-'),
		},
		{
			title: 'Akcje',
			key: 'actions',
			render: (_, row) => <Link to={`/panel/orders/${row.id}/${row.type}`}>Szczegóły</Link>,
		},
	];

	const getRowClassName = (row: FinancesOrderView) => {
		if (row.invoice) return 'grey';
		if (row.paid >= row.grossPrice) return 'green';

		return 'red';
	};

	const actions = [
		<Button key="2" size="large" type="primary" icon={<PrinterOutlined />} onClick={() => print()}>
			Drukuj
		</Button>,
	];

	const printRef = useRef<FinancesPrint>(null);
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});
	const print = () => {
		handlePrint();
	};

	if (!clients) {
		return (
			<Page title="Finanse" actions={actions}>
				<Skeleton active />
			</Page>
		);
	}

	return (
		<Page title="Finanse" actions={actions}>
			<div className="orders flex flex-dir">
				<div className="inputs flex flex-dir">
					<SearchSelector
						allowClear
						placeholder="Klient"
						id="id"
						elements={clients}
						value={clientId}
						onChange={clientIdChanged}
						buildElement={(client) => client.name}
						search={(search, client) => client.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())}
					/>
					<Space size={25}>
						<DatePicker
							size="large"
							value={from}
							onChange={(value) => {
								if (!value) {
									return;
								}

								fromChanged(value);
							}}
							allowClear={false}
						/>
						<DatePicker
							size="large"
							value={to}
							onChange={(value) => {
								if (!value) {
									return;
								}

								toChanged(value);
							}}
							allowClear={false}
						/>
					</Space>
					<Space size={25}>
						<Select size="large" placeholder="Status" value={paid} onChange={paidChanged}>
							<Select.Option value={1}>Zapłacone</Select.Option>
							<Select.Option value={0}>Niezapłacone</Select.Option>
						</Select>
						<Select size="large" placeholder="Typ płatności" value={paymentType} onChange={paymentTypeChanged}>
							<Select.Option value={PaymentType.transfer}>Przelew</Select.Option>
							<Select.Option value={PaymentType.cash}>Gotówka</Select.Option>
						</Select>
					</Space>
					<Button size="large" type="primary" icon={<CloseCircleOutlined />} onClick={clearFilters}>
						Wyczyść
					</Button>
				</div>

				<OrdersTable>
					<Table
						columns={columns}
						rowKey={(row) => `${row.id.toString()}-${row.type}`}
						rowClassName={getRowClassName}
						dataSource={orders}
						pagination={false}
						loading={!orders}
						scroll={{ x: 'max-content' }}
					/>
				</OrdersTable>

				<FinancesPrint ref={printRef} orders={orders} from={from} to={to} />
			</div>
		</Page>
	);
};
